/**
 * Saving and opening cards, side by side on My cards (made simpler 2026-10-17 - the owner:
 * "too engineered and complicated, save a card and open a card should be almost in the same
 * place"). Cards live only in this browser, which can lose them (cleared data, a lost phone,
 * Safari's seven-day rule), so one row of buttons offers what this browser can do:
 *   - Open a card file: one card, or a backup of many (local/card-file.ts);
 *   - Save all my cards: one file with every card - shared on a phone (Save to Files,
 *     iCloud Drive), downloaded on a computer - with a reminder once cards have changed
 *     since the last copy;
 *   - Keep in a folder (local/folder.ts), where the browser can write to one: its details
 *     open under the button;
 *   - on iPhone and iPad, a nudge to add FoldMyCard to the Home Screen, which takes it out
 *     of Safari's rule that clears unused sites after seven days.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from './api.ts';
import { download, share } from './card-pdf.tsx';
import { isTouchDevice } from './device.tsx';
import { CardFileError, openCardFile, saveBackupFile } from './local/card-file.ts';
import { getMeta, setMeta } from './local/db.ts';
import {
  chooseFolder, folderState, forgetFolder, LAST_BACKUP, onFolderState, reconnectFolder, sync, type FolderState,
} from './local/folder.ts';
import { cardDates, onCardEvent } from './local/store.ts';
import { navigate } from './router.tsx';
import { ago, t } from './strings.ts';

export function useFolderState(): FolderState {
  const [s, setS] = useState(folderState);
  useEffect(() => onFolderState(setS), []);
  return s;
}

const isIos = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const isInstalled = () => matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;
const HOME_TIP = 'fmc.homeScreenTipClosed';

/** Small line icons for the buttons. */
const ICON = {
  open: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />,
  save: <><path d="M12 4v11" /><path d="M7 10l5 5 5-5" /><path d="M5 19h14" /></>,
  folder: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 13l2 2 4-4" /></>,
};
const Icon = ({ k }: { k: keyof typeof ICON }) => <svg className="btn-icon" viewBox="0 0 24 24" aria-hidden="true">{ICON[k]}</svg>;

export function SaveOpenBar({ onCardsChanged }: { onCardsChanged: () => void }) {
  const folder = useFolderState();
  const [last, setLast] = useState<string | null>(null);
  const [changed, setChanged] = useState(0);
  const [cards, setCards] = useState(0);
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [ready, setReady] = useState<File | null>(null);
  const [folderOpen, setFolderOpen] = useState(false);
  const [tip, setTip] = useState(() => {
    try { return isIos() && !isInstalled() && localStorage.getItem(HOME_TIP) !== '1'; } catch { return isIos() && !isInstalled(); }
  });
  const cardFile = useRef<HTMLInputElement>(null);

  // how many cards changed since the last copy was made
  const [tick, setTick] = useState(0);
  useEffect(() => onCardEvent(() => setTick((n) => n + 1)), []);
  useEffect(() => {
    let live = true;
    (async () => {
      const at = (await getMeta<string>(LAST_BACKUP)) ?? null;
      const dates = await cardDates();
      if (!live) return;
      setLast(at);
      setCards(dates.size);
      setChanged([...dates.values()].filter((d) => !at || d > at).length);
    })().catch(() => {});
    return () => { live = false; };
  }, [tick, folder]);

  const run = (work: () => Promise<void>) => async () => {
    setBusy(true);
    setMsg({});
    try {
      await work();
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setMsg({ err: (err as Error).message });    // AbortError: the picker was closed
    } finally {
      setBusy(false);
    }
  };

  /** A .foldmycard file: one card - added or updated, then opened - or a backup of many, put back into the list. */
  const openFile = async (file: File) => {
    setOpening(true);
    setMsg({});
    try {
      const r = await openCardFile(file, (tid, paper, hash) => api.template(tid, paper, hash).then(() => true, () => false));
      if (!r.backup) return navigate(`/projects/${[...r.added, ...r.updated, ...r.kept][0]}`);
      onCardsChanged();
      setMsg({ ok: t('backupOpened', { added: r.added.length, updated: r.updated.length, kept: r.kept.length }) });
    } catch (err) {
      setMsg({ err: err instanceof CardFileError ? err.message : t('cardFileFailed') });
      if (!(err instanceof CardFileError)) console.error(err);
    } finally {
      setOpening(false);
    }
  };

  const shareBackup = async (file: File) => {
    const r = await share(file, file.name);
    if (r === 'needs-tap') return setReady(file);
    setReady(null);
    if (r === 'unsupported') download(file, file.name);
    if (r !== 'cancelled') await done();
  };
  const done = async () => {
    await setMeta(LAST_BACKUP, new Date().toISOString());
    setTick((n) => n + 1);
    setMsg({ ok: t('backupMadeCards') });
  };
  const backup = run(async () => {
    const b = await saveBackupFile();
    const file = new File([b.blob], b.name, { type: 'application/octet-stream' });
    // a phone keeps files through its share menu (Save to Files, iCloud Drive); a computer downloads
    if (isTouchDevice()) await shareBackup(file);
    else {
      download(file, file.name);
      await done();
    }
  });

  const on = folder.status === 'saved' || folder.status === 'saving';
  const lastCopy = last ? t('lastBackup', { when: ago(last) }) : t('noBackupYet');
  return (
    <div className="save-open">
      <div className="save-open-bar">
        <input ref={cardFile} type="file" hidden
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) void openFile(f); }} />
        <button onClick={() => cardFile.current?.click()} disabled={opening} title={t('openCardFileHint')}>
          <Icon k="open" />{opening ? t('loading') : t('openCardShort')}
        </button>
        <button onClick={backup} disabled={busy || cards === 0} title={`${t('backupAllHint')} ${lastCopy}`}>
          <Icon k="save" />{busy ? t('loading') : t('backupAll')}
        </button>
        {folder.status !== 'unsupported' && (
          folder.status === 'off' ? (
            <button onClick={run(chooseFolder)} disabled={busy} title={t('chooseFolderHint')}>
              <Icon k="folder" />{t('folderShort')}
            </button>
          ) : (
            <button aria-expanded={folderOpen} onClick={() => setFolderOpen(!folderOpen)}
              className={folder.status === 'needs-permission' || folder.status === 'error' ? 'warn-btn' : 'ok-btn'}>
              <Icon k="folder" />
              {on ? t('folderOnShort', { name: folder.name ?? '' })
                : folder.status === 'needs-permission' ? t('folderFixShort') : t('folderErrorShort')}
            </button>
          )
        )}
      </div>

      {folderOpen && folder.status !== 'off' && folder.status !== 'unsupported' && (
        <div className="folder-pop" role="region" aria-label={t('folderMenuTitle')}>
          {on && (
            <>
              <p className="ok-line">
                <span aria-hidden="true">✓</span> {t('savingToFolder', { name: folder.name ?? '' })}
                {folder.status === 'saving' ? <span className="muted small"> · {t('saving')}</span>
                  : folder.at ? <span className="muted small"> · {t('lastSaved', { when: ago(folder.at) })}</span> : null}
              </p>
              <p className="muted small">{t('folderOnHint')}</p>
              <div className="row">
                <button onClick={run(chooseFolder)} disabled={busy}>{t('changeFolder')}</button>
                <button onClick={run(async () => { await forgetFolder(); setFolderOpen(false); })} disabled={busy}>{t('stopFolder')}</button>
              </div>
            </>
          )}
          {folder.status === 'needs-permission' && (
            <>
              <p className="warn">{t('folderNeedsOk', { name: folder.name ?? '' })}</p>
              <p className="muted small">{t('folderNeedsOkHint')}</p>
              <div className="row">
                <button className="primary" onClick={run(reconnectFolder)} disabled={busy}>{t('reconnectFolder')}</button>
                <button onClick={run(async () => { await forgetFolder(); setFolderOpen(false); })} disabled={busy}>{t('stopFolder')}</button>
              </div>
            </>
          )}
          {folder.status === 'error' && (
            <>
              <p className="error">{t('folderError', { name: folder.name ?? '' })} <span className="small">{folder.error}</span></p>
              <div className="row">
                <button onClick={run(sync)} disabled={busy}>{t('tryAgain')}</button>
                <button onClick={run(chooseFolder)} disabled={busy}>{t('changeFolder')}</button>
              </div>
            </>
          )}
        </div>
      )}

      {!on && cards > 0 && changed > 0 && (
        <p className="save-reminder small">
          {changed === 1 ? t('oneCardNotBackedUp') : t('cardsNotBackedUp', { n: changed })} <span className="muted">{lastCopy}</span>
        </p>
      )}
      {ready && (
        <p className="ok small">{t('backupReady')} <button className="primary" onClick={() => void shareBackup(ready)}>{t('sharePdf')}</button></p>
      )}
      {msg.ok && <p className="ok small" role="status">{msg.ok}</p>}
      {msg.err && <p className="error small" role="alert">{msg.err}</p>}
      {tip && (
        <p className="tip small">
          {t('homeScreenTip')}{' '}
          <button className="link" onClick={() => { setTip(false); try { localStorage.setItem(HOME_TIP, '1'); } catch { /* fine */ } }}>
            {t('gotIt')}
          </button>
        </p>
      )}
    </div>
  );
}
